/**
 * Centralisation des routes lazy-chargées du backoffice.
 *
 * Pour chaque page :
 *  - Un import dynamique mémoïsé (la promesse est cachée → un seul download).
 *  - Le composant `lazy()` est exporté pour être utilisé dans App.tsx.
 *  - La fonction d'import elle-même est exposée via `ROUTE_PRELOADERS` pour
 *    permettre le préchargement au survol depuis la Sidebar (MT-5).
 *
 * Pourquoi un fichier séparé ?
 *  - Évite que `App.tsx` ait 80+ blocs `React.lazy(...)` qui rendent la lecture pénible.
 *  - Permet à la Sidebar de demander un `preloadRoute(path)` typé sans dupliquer
 *    la chaîne de chemin de l'import dynamique.
 */

import { ComponentType, lazy, LazyExoticComponent } from 'react';
import { createRecoverableLazyLoader } from '../utils/chunkErrorHandler';

type Loader = () => Promise<{ default: ComponentType<unknown> }>;

/**
 * Type plus permissif que `LazyExoticComponent<typeof Page>` :
 * on n'a pas accès aux types exports des pages depuis ici sans tirer
 * tout l'arbre, et React.lazy autorise n'importe quel `ComponentType`.
 */
type LazyPage = LazyExoticComponent<ComponentType<unknown>>;

/**
 * Mémoïse + recovery chunk (HTML-as-JS / hash obsolète après deploy).
 * Voir `createRecoverableLazyLoader`, invalide le cache si l'import échoue.
 */
const make = (loader: Loader): { Component: LazyPage; preload: Loader } => {
  const memoized = createRecoverableLazyLoader(loader);
  return {
    Component: lazy(memoized),
    preload: memoized,
  };
};

// ============================================================================
// PAGES PUBLIQUES
// ============================================================================

const _LandingPage = make(
  () => import('../pages/LandingPage') as ReturnType<Loader>
);
const _LandingPageV2 = make(
  () => import('../pages/LandingPageV2') as ReturnType<Loader>
);
const _LoginPage = make(() => import('../pages/LoginPage') as ReturnType<Loader>);
const _MaintenancePage = make(
  () => import('../pages/MaintenancePage') as ReturnType<Loader>
);
const _AdminHandoffPage = make(
  () => import('../pages/AdminHandoffPage') as ReturnType<Loader>
);
const _SignupUserPage = make(
  () => import('../pages/SignupUserPage') as ReturnType<Loader>
);
const _SignUpProPage = make(
  () => import('../pages/SignUpProfessionalPage') as ReturnType<Loader>
);
const _NoProProfile = make(() => import('../pages/NoProProfile') as ReturnType<Loader>);
const _NoPremiumMessaging = make(
  () => import('../pages/NoPremiumMessaging') as ReturnType<Loader>
);
const _TermsOfUse = make(() => import('../pages/TermsOfUse') as ReturnType<Loader>);
const _PrivacyPolicy = make(() => import('../pages/PrivacyPolicy') as ReturnType<Loader>);
const _DataDeletion = make(() => import('../pages/DataDeletion') as ReturnType<Loader>);
const _LegalPage = make(() => import('../pages/Legal') as ReturnType<Loader>);
const _GoogleAuthCallback = make(
  () => import('../components/GoogleAuthCallback') as ReturnType<Loader>
);
const _GoogleAccountLinkingCallbackPage = make(
  () => import('../pages/GoogleAccountLinkingCallbackPage') as ReturnType<Loader>
);
const _PasswordForgottenPage = make(
  () => import('../pages/PasswordForgotten/PasswordForgotten') as ReturnType<Loader>
);
const _ResetPasswordPage = make(
  () => import('../pages/ResetPassword/ResetPasswordPage') as ReturnType<Loader>
);
const _NotFoundPage = make(() => import('../pages/NotFoundPage') as ReturnType<Loader>);
const _CGV = make(() => import('../pages/CGV') as ReturnType<Loader>);
const _StripeSetupComplete = make(
  () => import('../pages/StripeSetupComplete') as ReturnType<Loader>
);

// SEO hubs marché (pays ≠ langue)
const _BelgiumHubPage = make(
  () => import('../pages/MarketLanding/BelgiumHubPage') as ReturnType<Loader>
);
const _BelgiumEducateurCaninPage = make(
  () => import('../pages/MarketLanding/BelgiumEducateurCaninPage') as ReturnType<Loader>
);
const _BelgiumPetSitterPage = make(
  () => import('../pages/MarketLanding/BelgiumPetSitterPage') as ReturnType<Loader>
);
const _NetherlandsHubPage = make(
  () => import('../pages/MarketLanding/NetherlandsHubPage') as ReturnType<Loader>
);
const _NetherlandsEducateurCaninPage = make(
  () => import('../pages/MarketLanding/NetherlandsEducateurCaninPage') as ReturnType<Loader>
);
const _NetherlandsPetSitterPage = make(
  () => import('../pages/MarketLanding/NetherlandsPetSitterPage') as ReturnType<Loader>
);

// SEO « verticales »
const _EducateurCaninPage = make(
  () => import('../pages/EducateurCaninPage') as ReturnType<Loader>
);
const _PromeneurCaninPage = make(
  () => import('../pages/PromeneurCaninPage') as ReturnType<Loader>
);
const _PetSitterPage = make(() => import('../pages/PetSitterPage') as ReturnType<Loader>);
const _LogicielDogSitterPage = make(
  () => import('../pages/LogicielDogSitterPage') as ReturnType<Loader>
);
const _EcoleClubCaninPage = make(
  () => import('../pages/EcoleClubCaninPage') as ReturnType<Loader>
);
const _PensionPage = make(() => import('../pages/PensionPage') as ReturnType<Loader>);
const _ComportementalistePage = make(
  () => import('../pages/ComportementalistePage') as ReturnType<Loader>
);
const _TailleursPage = make(() => import('../pages/TailleursPage') as ReturnType<Loader>);
const _LoisirCaninPage = make(() => import('../pages/LoisirCaninPage') as ReturnType<Loader>);

// Partenaires
const _PartenairesPublicPage = make(
  () => import('../pages/PartenairesPublicPage') as ReturnType<Loader>
);
const _PartenairesPage = make(
  () => import('../pages/PartenairesPage') as ReturnType<Loader>
);
const _PartenaireDetailPage = make(
  () => import('../pages/PartenaireDetailPage') as ReturnType<Loader>
);
const _LupiPage = make(() => import('../pages/LupiPage') as ReturnType<Loader>);
const _OsmozPage = make(() => import('../pages/OsmozPage') as ReturnType<Loader>);
const _OsmozInAppPage = make(
  () => import('../pages/OsmozInAppPage') as ReturnType<Loader>
);
const _MediavetPage = make(() => import('../pages/MediavetPage') as ReturnType<Loader>);
const _TresorsDUlyssePage = make(
  () => import('../pages/TresorsDUlyssePage') as ReturnType<Loader>
);
const _MfecPage = make(() => import('../pages/MfecPage') as ReturnType<Loader>);
const _AmbassadeurPage = make(
  () => import('../pages/AmbassadeurPage/AmbassadeurPage') as ReturnType<Loader>
);
_AmbassadeurPage.preload();

// Subscription
const _SubscriptionPage = make(
  () => import('../pages/SubscriptionPage') as ReturnType<Loader>
);
const _BoardingModuleOfferPage = make(
  () => import('../pages/BoardingModuleOfferPage') as ReturnType<Loader>
);
const _SubSuccessPage = make(
  () => import('../pages/SubSuccesPage') as ReturnType<Loader>
);
const _BoardingSubSuccessPage = make(
  () => import('../pages/BoardingSubSuccessPage') as ReturnType<Loader>
);
const _SubCancelPage = make(
  () => import('../pages/SubCancelPage') as ReturnType<Loader>
);
const _WelcomeTrialOfferPage = make(
  () => import('../pages/WelcomeTrialOfferPage') as ReturnType<Loader>
);
const _WelcomePricingTablePage = make(
  () => import('../pages/WelcomePricingTablePage') as ReturnType<Loader>
);
const _OffreConfigurationPage = make(
  () => import('../pages/OffreConfigurationPage') as ReturnType<Loader>
);
const _AcacedOfferPage = make(
  () => import('../pages/AcacedOfferPage') as ReturnType<Loader>
);
const _AcacedPage = make(
  () => import('../pages/AcacedPage') as ReturnType<Loader>
);
const _EFacturationOfferPage = make(
  () => import('../pages/EFacturationOfferPage') as ReturnType<Loader>
);
const _SoftwareComparisonPage = make(
  () => import('../pages/SoftwareComparisonPage') as ReturnType<Loader>
);
const _SoftwareComparisonPetSitterPage = make(
  () => import('../pages/SoftwareComparisonPetSitterPage') as ReturnType<Loader>
);
const _SoftwareComparisonPensionPage = make(
  () => import('../pages/SoftwareComparisonPensionPage') as ReturnType<Loader>
);
const _MigrationPlaybookPage = make(
  () => import('../pages/MigrationPlaybookPage') as ReturnType<Loader>
);
const _ToolResourcePage = make(
  () => import('../pages/ToolResourcePage') as ReturnType<Loader>
);
const _AlternativesHubPage = make(
  () => import('../pages/AlternativesHubPage') as ReturnType<Loader>
);
const _VsCanisuiviPage = make(
  () => import('../pages/VsCanisuiviPage') as ReturnType<Loader>
);
const _VsHunimalisPage = make(
  () => import('../pages/VsHunimalisPage') as ReturnType<Loader>
);
const _VsKookiePage = make(() => import('../pages/VsKookiePage') as ReturnType<Loader>);
const _FaqHubPage = make(() => import('../pages/FaqHubPage') as ReturnType<Loader>);
const _AvisRintintinProPage = make(
  () => import('../pages/AvisRintintinProPage') as ReturnType<Loader>
);

// Blog
const _BlogPage = make(() => import('../pages/Blog/BlogPage') as ReturnType<Loader>);
const _BlogArticlePage = make(
  () => import('../pages/Blog/BlogArticlePage') as ReturnType<Loader>
);

// Pages Fonctionnalités (SEO), `pages/Features` est un barrel.
// On importe le module entier puis on extrait le named export voulu : chaque
// composant peut être préchargé indépendamment, et webpack créera autant
// de chunks que d'imports dynamiques distincts.
const _FeaturesPage = make(
  () =>
    import('../pages/Features').then((m) => ({ default: m.FeaturesPage })) as ReturnType<Loader>
);
const _PlanningReservationsPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.PlanningReservationsPage,
    })) as ReturnType<Loader>
);
const _MessagingFeaturePage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.MessagingFeaturePage,
    })) as ReturnType<Loader>
);
const _PaiementFacturationPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.PaiementFacturationPage,
    })) as ReturnType<Loader>
);
const _ForfaitsCreditsPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.ForfaitsCreditsPage,
    })) as ReturnType<Loader>
);
const _CartesCadeauxPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.CartesCadeauxPage,
    })) as ReturnType<Loader>
);
const _EmailsProPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.EmailsProPage,
    })) as ReturnType<Loader>
);
const _GroupesClientsPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.GroupesClientsPage,
    })) as ReturnType<Loader>
);
const _GestionClientsAnimauxPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.GestionClientsAnimauxPage,
    })) as ReturnType<Loader>
);
const _StatistiquesPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.StatistiquesPage,
    })) as ReturnType<Loader>
);
const _ProfilProfessionnelPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.ProfilProfessionnelPage,
    })) as ReturnType<Loader>
);
const _PensionHebergementPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.PensionHebergementPage,
    })) as ReturnType<Loader>
);
const _ApplicationMobilePage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.ApplicationMobilePage,
    })) as ReturnType<Loader>
);
const _DoubleSyncGooglePage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.DoubleSyncGooglePage,
    })) as ReturnType<Loader>
);
const _FraisKilometriquesPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.FraisKilometriquesPage,
    })) as ReturnType<Loader>
);
const _RappelsSmsPage = make(
  () =>
    import('../pages/Features').then((m) => ({
      default: m.RappelsSmsPage,
    })) as ReturnType<Loader>
);

const _LanguageTestPage = make(
  () => import('../pages/LanguageTestPage') as ReturnType<Loader>
);

const _WidgetLabPage = make(
  () => import('../pages/WidgetLabPage/WidgetLabPage') as ReturnType<Loader>
);

// ============================================================================
// PAGES PRIVÉES (/app)
// ============================================================================

const _AccueilPage = make(() => import('../pages/AccueilPage') as ReturnType<Loader>);
const _StartupPage = make(() => import('../pages/StartupPage') as ReturnType<Loader>);
const _PlanningPage = make(() => import('../pages/PlanningPage') as ReturnType<Loader>);
const _ReservationsPage = make(
  () => import('../pages/ReservationsPage/ReservationsPage') as ReturnType<Loader>
);
const _ProfilePage = make(
  () => import('../pages/ProfilePage/ProfilePage') as ReturnType<Loader>
);
const _ProfilePageV2 = make(
  () => import('../pages/ProfilePageV2/ProfilePageV2') as ReturnType<Loader>
);
const _ClientSegmentsPage = make(
  () => import('../pages/ClientSegmentsPage/ClientSegmentsPage') as ReturnType<Loader>
);
const _ClientsChiensPage = make(
  () => import('../pages/ClientsChiensPage') as ReturnType<Loader>
);
const _AnimalDetailPage = make(
  () => import('../pages/AnimalDetailPage') as ReturnType<Loader>
);
const _AnimalFollowUpPage = make(
  () => import('../pages/AnimalFollowUpPage') as ReturnType<Loader>
);
const _FollowHubPage = make(
  () => import('../features/followEngine/FollowHubPage') as ReturnType<Loader>
);
const _ProprioDetailPage = make(
  () => import('../pages/ProprioDetailPageV2') as ReturnType<Loader>
);
const _AvailabilityPage = make(
  () => import('../pages/AvailabilityPage') as ReturnType<Loader>
);
const _BookingDetailPage = make(
  () => import('../pages/BookingDetailPage') as ReturnType<Loader>
);
const _SessionDetailPage = make(
  () => import('../pages/SessionDetailPage') as ReturnType<Loader>
);
const _AllNotesPage = make(() => import('../pages/AllNotesPage') as ReturnType<Loader>);
const _ServicesPage = make(
  () => import('../pages/ServicesPage/ServicesPage') as ReturnType<Loader>
);
const _ForfaitsPage = make(
  () => import('../pages/ForfaitsPages/ForfaitsPage') as ReturnType<Loader>
);
const _SettingsPage = make(() => import('../pages/SettingsPage') as ReturnType<Loader>);
const _BoardingPage = make(
  () => import('../pages/Boarding/BoardingPage') as ReturnType<Loader>
);
const _StayDetailPage = make(
  () => import('../pages/Boarding/StayDetailPage') as ReturnType<Loader>
);
const _UnavailabilitiesPage = make(
  () => import('../pages/UnavailabilitiesPage') as ReturnType<Loader>
);
const _BillingPage = make(() => import('../pages/BillingPage') as ReturnType<Loader>);
const _InvoiceDetailPage = make(
  () => import('../pages/InvoiceDetailPage') as ReturnType<Loader>
);
const _QuotesPage = make(() => import('../pages/QuotesPage') as ReturnType<Loader>);
const _AccountingPage = make(
  () => import('../pages/AccountingPage') as ReturnType<Loader>
);
const _FinancePage = make(() => import('../pages/FinancePage') as ReturnType<Loader>);
const _MarketingToolsPage = make(
  () => import('../pages/MarketingToolsPage') as ReturnType<Loader>
);
const _ProStatsPage = make(() => import('../pages/ProStatsPage') as ReturnType<Loader>);
const _ProStatsPageV2 = make(
  () => import('../pages/ProStatsPageV2') as ReturnType<Loader>
);
const _TeamPage = make(() => import('../pages/TeamPage') as ReturnType<Loader>);
const _DesignSystemPage = make(
  () => import('../pages/DesignSystemPage') as ReturnType<Loader>
);
const _SmsManagementPage = make(
  () => import('../pages/SmsManagementPage') as ReturnType<Loader>
);
const _LaikaPage = make(() => import('../pages/LaikaPage') as ReturnType<Loader>);
const _BackOfficeMessagingPage = make(
  () =>
    import('../pages/BackOfficeMessaging/BackOfficeMessagingPage') as ReturnType<Loader>
);
const _NotificationHistoryPage = make(
  () =>
    import('../pages/NotificationHistoryPage/NotificationHistoryPage') as ReturnType<Loader>
);

// Association
const _AssociationDashboard = make(
  () => import('../pages/AssociationDashboard') as ReturnType<Loader>
);
const _AssociationPlanningPage = make(
  () => import('../pages/AssociationPlanningPage') as ReturnType<Loader>
);
const _AssociationMembersPage = make(
  () => import('../pages/AssociationMembersPage') as ReturnType<Loader>
);
const _AssociationMemberProfilePage = make(
  () => import('../pages/AssociationMemberProfilePage') as ReturnType<Loader>
);
const _AssociationClientsPage = make(
  () => import('../pages/AssociationClientsPage') as ReturnType<Loader>
);
const _AssociationEventsPage = make(
  () => import('../pages/AssociationEventsPage') as ReturnType<Loader>
);
const _AssociationMessagingPage = make(
  () => import('../pages/AssociationMessagingPage') as ReturnType<Loader>
);
const _AssociationCGVPage = make(
  () => import('../pages/AssociationCGVPage') as ReturnType<Loader>
);
const _AssociationSettingsPage = make(
  () => import('../pages/AssociationSettingsPage') as ReturnType<Loader>
);

// ============================================================================
// EXPORTS PUBLICS, composants à utiliser dans <Routes> dans App.tsx
// ============================================================================

export const LandingPage = _LandingPage.Component;
export const LandingPageV2 = _LandingPageV2.Component;
export const LoginPage = _LoginPage.Component;
export const MaintenancePage = _MaintenancePage.Component;
export const AdminHandoffPage = _AdminHandoffPage.Component;
export const SignupUserPage = _SignupUserPage.Component;
export const SignUpProPage = _SignUpProPage.Component;
export const NoProProfile = _NoProProfile.Component;
export const NoPremiumMessaging = _NoPremiumMessaging.Component;
export const TermsOfUse = _TermsOfUse.Component;
export const PrivacyPolicy = _PrivacyPolicy.Component;
export const DataDeletion = _DataDeletion.Component;
export const LegalPage = _LegalPage.Component;
export const GoogleAuthCallback = _GoogleAuthCallback.Component;
export const GoogleAccountLinkingCallbackPage = _GoogleAccountLinkingCallbackPage.Component;
export const PasswordForgottenPage = _PasswordForgottenPage.Component;
export const ResetPasswordPage = _ResetPasswordPage.Component;
export const NotFoundPage = _NotFoundPage.Component;
export const CGV = _CGV.Component;
export const StripeSetupComplete = _StripeSetupComplete.Component;

export const BelgiumHubPage = _BelgiumHubPage.Component;
export const BelgiumEducateurCaninPage = _BelgiumEducateurCaninPage.Component;
export const BelgiumPetSitterPage = _BelgiumPetSitterPage.Component;
export const NetherlandsHubPage = _NetherlandsHubPage.Component;
export const NetherlandsEducateurCaninPage = _NetherlandsEducateurCaninPage.Component;
export const NetherlandsPetSitterPage = _NetherlandsPetSitterPage.Component;

export const EducateurCaninPage = _EducateurCaninPage.Component;
export const PromeneurCaninPage = _PromeneurCaninPage.Component;
export const PetSitterPage = _PetSitterPage.Component;
export const LogicielDogSitterPage = _LogicielDogSitterPage.Component;
export const EcoleClubCaninPage = _EcoleClubCaninPage.Component;
export const PensionPage = _PensionPage.Component;
export const ComportementalistePage = _ComportementalistePage.Component;
export const TailleursPage = _TailleursPage.Component;
export const LoisirCaninPage = _LoisirCaninPage.Component;

export const PartenairesPublicPage = _PartenairesPublicPage.Component;
export const PartenairesPage = _PartenairesPage.Component;
export const PartenaireDetailPage = _PartenaireDetailPage.Component;
export const LupiPage = _LupiPage.Component;
export const OsmozPage = _OsmozPage.Component;
export const OsmozInAppPage = _OsmozInAppPage.Component;
export const MediavetPage = _MediavetPage.Component;
export const TresorsDUlyssePage = _TresorsDUlyssePage.Component;
export const MfecPage = _MfecPage.Component;
export const AmbassadeurPage = _AmbassadeurPage.Component;

export const SubscriptionPage = _SubscriptionPage.Component;
export const BoardingModuleOfferPage = _BoardingModuleOfferPage.Component;
export const SubSuccessPage = _SubSuccessPage.Component;
export const BoardingSubSuccessPage = _BoardingSubSuccessPage.Component;
export const SubCancelPage = _SubCancelPage.Component;
export const WelcomeTrialOfferPage = _WelcomeTrialOfferPage.Component;
export const WelcomePricingTablePage = _WelcomePricingTablePage.Component;
export const OffreConfigurationPage = _OffreConfigurationPage.Component;
export const EFacturationOfferPage = _EFacturationOfferPage.Component;
export const AcacedOfferPage = _AcacedOfferPage.Component;
export const AcacedPage = _AcacedPage.Component;
export const SoftwareComparisonPage = _SoftwareComparisonPage.Component;
export const SoftwareComparisonPetSitterPage = _SoftwareComparisonPetSitterPage.Component;
export const SoftwareComparisonPensionPage = _SoftwareComparisonPensionPage.Component;
export const MigrationPlaybookPage = _MigrationPlaybookPage.Component;
export const ToolResourcePage = _ToolResourcePage.Component;
export const AlternativesHubPage = _AlternativesHubPage.Component;
export const VsCanisuiviPage = _VsCanisuiviPage.Component;
export const VsHunimalisPage = _VsHunimalisPage.Component;
export const VsKookiePage = _VsKookiePage.Component;
export const FaqHubPage = _FaqHubPage.Component;
export const AvisRintintinProPage = _AvisRintintinProPage.Component;

export const BlogPage = _BlogPage.Component;
export const BlogArticlePage = _BlogArticlePage.Component;

export const FeaturesPage = _FeaturesPage.Component;
export const PlanningReservationsPage = _PlanningReservationsPage.Component;
export const MessagingFeaturePage = _MessagingFeaturePage.Component;
export const PaiementFacturationPage = _PaiementFacturationPage.Component;
export const ForfaitsCreditsPage = _ForfaitsCreditsPage.Component;
export const CartesCadeauxPage = _CartesCadeauxPage.Component;
export const EmailsProPage = _EmailsProPage.Component;
export const GroupesClientsPage = _GroupesClientsPage.Component;
export const GestionClientsAnimauxPage = _GestionClientsAnimauxPage.Component;
export const StatistiquesPage = _StatistiquesPage.Component;
export const ProfilProfessionnelPage = _ProfilProfessionnelPage.Component;
export const PensionHebergementPage = _PensionHebergementPage.Component;
export const ApplicationMobilePage = _ApplicationMobilePage.Component;
export const DoubleSyncGooglePage = _DoubleSyncGooglePage.Component;
export const FraisKilometriquesPage = _FraisKilometriquesPage.Component;
export const RappelsSmsPage = _RappelsSmsPage.Component;
export const LanguageTestPage = _LanguageTestPage.Component;
export const WidgetLabPage = _WidgetLabPage.Component;

export const AccueilPage = _AccueilPage.Component;
export const StartupPage = _StartupPage.Component;
export const PlanningPage = _PlanningPage.Component;
export const ReservationsPage = _ReservationsPage.Component;
export const ProfilePage = _ProfilePage.Component;
export const ProfilePageV2 = _ProfilePageV2.Component;
export const ClientSegmentsPage = _ClientSegmentsPage.Component;
export const ClientsChiensPage = _ClientsChiensPage.Component;
export const AnimalDetailPage = _AnimalDetailPage.Component;
export const AnimalFollowUpPage = _AnimalFollowUpPage.Component;
export const FollowHubPage = _FollowHubPage.Component;
export const ProprioDetailPage = _ProprioDetailPage.Component;
export const AvailabilityPage = _AvailabilityPage.Component;
export const BookingDetailPage = _BookingDetailPage.Component;
export const SessionDetailPage = _SessionDetailPage.Component;
export const AllNotesPage = _AllNotesPage.Component;
export const ServicesPage = _ServicesPage.Component;
export const ForfaitsPage = _ForfaitsPage.Component;
export const SettingsPage = _SettingsPage.Component;
export const BoardingPage = _BoardingPage.Component;
export const StayDetailPage = _StayDetailPage.Component;
export const UnavailabilitiesPage = _UnavailabilitiesPage.Component;
export const BillingPage = _BillingPage.Component;
export const InvoiceDetailPage = _InvoiceDetailPage.Component;
export const QuotesPage = _QuotesPage.Component;
export const AccountingPage = _AccountingPage.Component;
export const FinancePage = _FinancePage.Component;
export const MarketingToolsPage = _MarketingToolsPage.Component;
export const ProStatsPage = _ProStatsPage.Component;
export const ProStatsPageV2 = _ProStatsPageV2.Component;
export const TeamPage = _TeamPage.Component;
export const DesignSystemPage = _DesignSystemPage.Component;
export const SmsManagementPage = _SmsManagementPage.Component;
export const LaikaPage = _LaikaPage.Component;
export const BackOfficeMessagingPage = _BackOfficeMessagingPage.Component;
export const NotificationHistoryPage = _NotificationHistoryPage.Component;

export const AssociationDashboard = _AssociationDashboard.Component;
export const AssociationPlanningPage = _AssociationPlanningPage.Component;
export const AssociationMembersPage = _AssociationMembersPage.Component;
export const AssociationMemberProfilePage = _AssociationMemberProfilePage.Component;
export const AssociationClientsPage = _AssociationClientsPage.Component;
export const AssociationEventsPage = _AssociationEventsPage.Component;
export const AssociationMessagingPage = _AssociationMessagingPage.Component;
export const AssociationCGVPage = _AssociationCGVPage.Component;
export const AssociationSettingsPage = _AssociationSettingsPage.Component;

// ============================================================================
// PRELOAD MAP : utilisé par la Sidebar pour précharger au survol (MT-5)
// ============================================================================

/**
 * Mapping <chemin de route> → fonction de préchargement.
 *
 * La Sidebar lit ce mapping pour précharger le chunk d'une route quand
 * l'utilisateur survole/focus le lien. Si la clé n'existe pas, le hover
 * ne déclenche simplement rien (pas d'erreur). On limite volontairement
 * aux routes principales du menu pour ne pas saturer la bande passante
 * avec des préchargements peu pertinents.
 */
export const ROUTE_PRELOADERS: Record<string, () => Promise<unknown>> = {
  '/app': _AccueilPage.preload,
  '/app/demarrage': _StartupPage.preload,
  '/app/planning': _PlanningPage.preload,
  '/app/planning/unavailabilities': _UnavailabilitiesPage.preload,
  '/app/reservations': _ReservationsPage.preload,
  '/app/services': _ServicesPage.preload,
  '/app/forfaits': _ForfaitsPage.preload,
  '/app/credits': _ForfaitsPage.preload,
  '/app/cartes-cadeaux': _ForfaitsPage.preload,
  '/app/abonnements': _ForfaitsPage.preload,
  '/app/clients': _ClientsChiensPage.preload,
  '/app/suivis': _FollowHubPage.preload,
  '/app/clients/segments': _ClientSegmentsPage.preload,
  '/app/chiens': _ClientsChiensPage.preload,
  '/app/clients-chiens': _ClientsChiensPage.preload,
  '/app/equipe': _TeamPage.preload,
  '/app/notes': _AllNotesPage.preload,
  '/app/billing': _BillingPage.preload,
  '/app/quotes': _QuotesPage.preload,
  '/app/devis': _QuotesPage.preload,
  '/app/comptabilite': _AccountingPage.preload,
  '/app/finance': _FinancePage.preload,
  '/app/marketing': _MarketingToolsPage.preload,
  '/app/stats': _ProStatsPageV2.preload,
  '/app/stats/v1': _ProStatsPage.preload,
  '/app/stats/v2': _ProStatsPageV2.preload,
  '/app/pension': _BoardingPage.preload,
  '/app/messaging': _BackOfficeMessagingPage.preload,
  '/app/messages': _BackOfficeMessagingPage.preload,
  '/app/profil': _ProfilePage.preload,
  '/app/profil-v2': _ProfilePageV2.preload,
  '/app/settings': _SettingsPage.preload,
  '/app/subscription': _SubscriptionPage.preload,
  '/app/subscription/hebergement': _BoardingModuleOfferPage.preload,
  '/app/acaced': _AcacedPage.preload,
  '/app/offre-essai': _WelcomeTrialOfferPage.preload,
  '/app/offre-essai/prix': _WelcomePricingTablePage.preload,
  '/app/associations': _AssociationDashboard.preload,
  '/app/laika': _LaikaPage.preload,
  '/app/sms': _SmsManagementPage.preload,
  '/app/notifications': _NotificationHistoryPage.preload,
  '/app/partenaires': _PartenairesPage.preload,
  '/app/partenaires/lupi': _LupiPage.preload,
  '/app/partenaires/osmoz': _OsmozInAppPage.preload,
  '/app/partenaires/mediavet': _MediavetPage.preload,
  '/app/partenaires/tresors-d-ulysse': _TresorsDUlyssePage.preload,
  '/app/partenaires/mfec': _MfecPage.preload,
  '/app/ambassadeur': _AmbassadeurPage.preload,
};

/**
 * Helper utilisable directement par la Sidebar :
 *   <NavLink onMouseEnter={() => preloadRoute('/app/planning')}>...</NavLink>
 *
 * Catch silencieux : un échec de préchargement n'est jamais bloquant
 * (l'utilisateur déclenchera de toutes façons le chargement réel au clic).
 */
export const preloadRoute = (path: string): void => {
  const loader = ROUTE_PRELOADERS[path];
  if (!loader) return;
  loader().catch(() => {
    /* swallow, le clic réel rejouera l'import */
  });
};
